// Tipos de Material N1
export type TipoMaterialN1 = 'perfis' | 'componentes' | 'kits' | 'superficies' | 'vidros' | 'acabamentos';

// Mapeamento de tipos para labels
export const TIPO_MATERIAL_LABELS: Record<TipoMaterialN1, string> = {
  perfis: 'PERFIS',
  componentes: 'COMPONENTES',
  kits: 'KITS',
  superficies: 'SUPERFÍCIES',
  vidros: 'VIDROS',
  acabamentos: 'ACABAMENTOS',
};

// Origem do registro N1 (FASE 05 — Q1/PO 2026-05-13)
// "Empresa" = registro criado por alguma empresa (Editavel=true): permite editar/duplicar/excluir.
// "BaseMof" = registro mestre do MOF (Editavel=false): somente visualizar/duplicar.
export type OrigemProdutoN1 = 'Empresa' | 'BaseMof';

// Produto na Listagem
export interface IProdutoListagemN1 {
  id: number;
  codigo: string;
  descricao: string;
  referencia: string;
  linha: string;
  status: 'ativo' | 'descontinuado' | 'inativo';
  // FASE 05 — 4ª validação/D3 2026-07-30: 'indisponivel' = id 4 do legado (fora do fluxo
  // de transições; só exibição para os registros antigos).
  situacao: 'em_edicao' | 'em_homologacao' | 'homologado' | 'disponivel_mercado' | 'indisponivel';
  urlImagem?: string;
  temImagem: boolean;
  origem: OrigemProdutoN1;
  editavel: boolean;
}

// Filtros
export interface IFiltrosListagemN1 {
  codigo?: string;
  descricao?: string;
  referencia?: string;
  linha?: string;
  status?: string[];
  situacao?: string[];
}

// Ordenação
export type CampoOrdenacao = 'codigo' | 'descricao' | 'referencia' | 'linha' | 'status' | 'situacao';

export interface IOrdenacaoListagemN1 {
  campo: CampoOrdenacao | null;
  direcao: 'asc' | 'desc' | null;
}

// Paginação
export interface IPaginacaoListagemN1 {
  page: number;      // 0-indexed
  limit: number;     // Default: 100
  total: number;
}

// Resposta da API
export interface IListarProdutosN1Response {
  items: IProdutoListagemN1[];
  total: number;
  page: number;
  limit: number;
}

// Tipo Material Permitido
export interface ITipoMaterialPermitido {
  idTipoMaterial: number;
  tipoMaterial: TipoMaterialN1;
  podeVisualizar: boolean;
  podeEditar: boolean;
  podeIncluir: boolean;
  podeImportar: boolean;
}

// Resposta de Tipos Permitidos
export interface ITiposPermitidosResponse {
  tipos: ITipoMaterialPermitido[];
  // FASE 05 — 2ª validação/PO 2026-07-24: empresa com a chave "apenas para consulta".
  apenasConsulta: boolean;
  permissoes: {
    podeCadastrar: boolean;
    podeImportar: boolean;
    podeExportar: boolean;
  };
}

// Ação em Lote
export interface IAcaoEmLoteRequest {
  idsProdutos: number[];
  idEmpresa: number;
}

export interface IAcaoEmLoteResponse {
  sucesso: boolean;
  total: number;
  mensagem?: string;
}

// Importação
export interface IImportarProdutosRequest {
  tipoMaterial: TipoMaterialN1;
  idEmpresa: number;
  arquivo: File;
}

export interface IResultadoImportacao {
  sucesso: boolean;
  linhasAceitas: number;
  linhasRecusadas: number;
  erros?: {
    linha: number;
    mensagem: string;
  }[];
  idImportacao?: number;
}

// Histórico de Importação
export interface IHistoricoImportacao {
  idImportacao: number;
  tipoMaterial: TipoMaterialN1;
  nomeArquivo: string;
  dataHoraImportacao: string;
  linhasAceitas: number;
  linhasRecusadas: number;
  situacao: 'em_edicao' | 'em_homologacao' | 'homologado' | 'disponivel_mercado';
  logErros?: string;
}

// Exportação
export interface IExportarProdutosRequest {
  tipoMaterial: TipoMaterialN1;
  filtros?: IFiltrosListagemN1;
  // FASE 05 — 3ª validação/PO 2026-07-28 (Frente 8): exporta o mesmo universo da tela
  // (publicados com a chave desligada, em edição com a chave ligada).
  modoEdicao?: boolean;
}

// Estado do Formulário
export interface IListagemMaterialState {
  tipoMaterialSelecionado: TipoMaterialN1 | null;
  modoEdicaoAtivo: boolean;
  itensSelecionados: number[];
  filtros: IFiltrosListagemN1;
  ordenacao: IOrdenacaoListagemN1;
  paginacao: IPaginacaoListagemN1;
  produtos: IProdutoListagemN1[];
  isLoading: boolean;
}

// Opções de Status
// FASE 05 — 2ª validação/PO 2026-07-24 (Frente 7, P5): só Ativo/Descontinuado no front.
export const OPCOES_STATUS = [
  { value: 'ativo', label: 'Ativo' },
  { value: 'descontinuado', label: 'Descontinuado' },
] as const;

// Opções de Situação
export const OPCOES_SITUACAO = [
  { value: 'em_edicao', label: 'Em edição' },
  { value: 'em_homologacao', label: 'Em homologação' },
  { value: 'homologado', label: 'Homologado' },
  { value: 'disponivel_mercado', label: 'Disponível mercado' },
  // FASE 05 — 4ª validação/D3 2026-07-30: estado legado (id 4), apenas para exibição/filtro
  // dos registros antigos — os botões de fluxo nunca gravam este estado.
  { value: 'indisponivel', label: 'Indisponível' },
] as const;
